'use strict';
import { AfterViewInit, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import * as GC from '@grapecity/spread-sheets';
import { CanDeactivateGuard } from '@guards/can-deactivate/can-deactivate-guard.service';
import { clone, isEmpty } from '@helpers/arrays';
import { backoff } from '@helpers/backoff';
import { clipboardClearOnCut } from '@helpers/clipboard-events';
import { bindSpreadJSEventsToSheet, mapActionKeys } from '@helpers/spread/map-action-keys';
import { resumeAll } from '@helpers/spread/resume-all';
import * as SheetFunctions from '@helpers/spread/sheet-functions';
import { checkDropdownValues } from '@helpers/spread/sheet-functions';
import { suspendAll } from '@helpers/spread/suspend-all';
import { DialogSaveObject } from '@models/dialog-save-object';
import { ExogenousLine } from '@models/exogenous-lines';
import { ExogenousResponse } from '@models/exogenous-response';
import { HoverNotification } from '@models/hover-notification';
import { isLiquidityAdmin } from '@models/user-level';
import { OpenIdConnectService } from '@services/auth/open-id-connect.service';
import { PermissionsService } from '@services/auth/permissions.service';
import { ExogenousLinesService } from '@services/exogenous-lines/exogenous-lines.service';
import { LiquiditySourcesService } from '@services/liquidity-sources/liquidity-sources.service';
import { MenuService } from '@services/menu/menu.service';
import { NotificationsService } from '@services/notifications/notifications.service';
import { setDefaultBookOptions, setDefaultSheetOptions } from '@shared/spreadsheet/base';
import { SpreadContextMenu } from '@shared/spreadsheet/protection';
import { spreadsheetConfig } from '@shared/spreadsheet/spreadsheet-config';
import { Toast } from 'ngx-toastr';
import { forkJoin, fromEvent, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import * as SPREAD_CONFIG_EXOG from './exogenous.spread.config';
import {
  COL_ACCOUNT_NAME,
  COL_ACCOUNT_NUM,
  COL_CALCULATE_GRP,
  COL_DISPLAY_FORMAT,
  COL_DISPLAY_IN_SUMMARY,
  COL_DISPLAY_ORDER,
  COL_FAIL_OPERATOR,
  COL_GROUP_ID,
  COL_MNEMONIC,
  COL_RISK_LIMIT,
  COL_SIMULATION_ORDER,
} from './exogenous.spread.config';
import { ToastLevel } from '@shared/toasterSetup';

const EMPTY_BOTTOM_ROWS = 2;
const MINIMUM_ROWS = SPREAD_CONFIG_EXOG.SpreadConfig.MINIMUM_ROWS;

@HostListener('window:resize', ['$event'])
@Component({
  selector: 'app-exogenous-lines',
  templateUrl: './exogenous-lines.component.html',
  styleUrls: ['./exogenous-lines.component.scss'],
})
export class ExogenousLinesComponent implements OnInit, OnDestroy, CanDeactivateGuard, AfterViewInit {
  /*
      =================
          Properties
      =================
  */

  public spreadConfig = SPREAD_CONFIG_EXOG.SpreadConfig;
  // Hover Messages
  public hoverMessage = '';
  public hoverTitle = '';
  public hoverValue = '';

  // Spread
  private readonly _keys = Object.keys(new ExogenousLine()).sort();
  public categoryMapping = {
    0: this._keys[0], // Account_Number
    1: this._keys[1], // Calculate_Group
    2: this._keys[2], // Description
    3: this._keys[3], // Display_Format
    4: this._keys[4], // Display_In_Summary
    5: this._keys[5], // Display_Order
    6: this._keys[6], // Fail_Operator
    7: this._keys[7], // Group_ID
    8: this._keys[8], // Mnemonic
    9: this._keys[9], // Name
    10: this._keys[10], // Risk_Limit
    11: this._keys[11], // Simulation_Order
  };

  public storedExogenousLines: Array<ExogenousResponse> = [];
  private editedExogenousLines: Array<ExogenousResponse> = [];
  private newExogenousLines: Array<ExogenousResponse> = [];
  private deletedExogenousLines: Array<ExogenousResponse> = [];

  public changedLines = false;

  public isLoading = false;
  public isSaving = false;
  private willCauseDataLoss = false;
  private isDeletionInProgress = false;

  private spread: GC.Spread.Sheets.Workbook = null;
  private sheet: GC.Spread.Sheets.Worksheet;
  public data: ExogenousResponse[] = [];
  private validationMessages = {};
  public notification: HoverNotification = {};
  private activeRow = -1;

  public isEditing = false;
  isBalanceSheetSaved = true;
  public validSheet = false;
  public invalidRows = [];
  private groupIDList = <any>['']; // Blank is an option

  @ViewChild('spreadContainer', { static: false }) container: ElementRef;
  private _saveEnabled = false;
  private destroyed$ = new Subject();

  private hasInvalidations = false;

  private userRoleCanEdit: boolean;

  public saveEnabled = async (): Promise<boolean> => {
    const newCondition = this.validSheet && !this.isSaving;

    if (newCondition !== this._saveEnabled) {
      this._saveEnabled = newCondition;
    }
    return newCondition;
  };

  /*
     =========================
       Constructor & onInit
     =========================
 */
  constructor(
    public menu: MenuService,
    private exogService: ExogenousLinesService,
    private auth: OpenIdConnectService,
    private notifications: NotificationsService,
    private zone: NgZone,
    private permissions: PermissionsService,
    // ToDo: This service is used as helper. We should refactorize it to centralize the shared methods in one generic service
    private liqSrcService: LiquiditySourcesService
  ) {}

  ngOnInit() {
    this.userRoleCanEdit = this.permissions.getPermission('ModelStructureExogenousLines') === 'W';
    this.menu.isSidePanelVisible = true;
    this.isLoading = true;
    this.loadData();
  }

  ngAfterViewInit(): void {
    // TODO: Encapsulate all the block inside initListeners method

    // Push to the array of subscription to be able to unsubscribe
    // Mouse Event Subscription
    fromEvent(this.container.nativeElement, 'mousemove')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event: MouseEvent) =>
        // this.onMouseMove(e.layerX, e.layerY)
        this.onMouseMove(event)
      );

    // Dialog Save Button changes (listenToDialogSaveButton)
    this.menu.dialogSaveButton$
      .pipe(
        takeUntil(this.destroyed$),
        backoff(3, 250),
        debounceTime(50),
        distinctUntilChanged(),
        catchError(err => of([]))
      )
      .subscribe((val: DialogSaveObject) => {
        if (val.page === 'exogenous-lines') {
          // Discard lost-of-data changes functionality
          if (val.source === `${val.page}-discard`) {
            this.menu.dialogSaveButton = { ...this.menu.dialogSaveButton, source: `${val.page}-discarded` };
            this.discardChanges();
          }
          // Only respond to click event if was triggered for this page
          if (val.clicked && val.enabled) {
            this.saveData();
            this.reCheck(`save`);
            this.isValidSheet();
          }
        }
      });

    this.menu.deactivatePopupClosed$.pipe(takeUntil(this.destroyed$)).subscribe(popupResult => {
      if (popupResult === false) {
        this.spread.focus();
      }
    });

    // Ctrl+V and Right Click paste (It emits as Ctrl+V)
    fromEvent(document.getElementById(spreadsheetConfig.containerParentId), 'keyup')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event: KeyboardEvent) => {
        if (this.userRoleCanEdit) {
          const pasted = event && ['KeyV'].includes(event.code) && event.ctrlKey;
          if (pasted) {
            setTimeout(() => this.reCheck(`ClipboardPasted`));
          }
        }
      });

    // Should be executed after view has been initialized
    if (this.spread) {
      this.spread.focus();
    }
  }

  ngOnDestroy(): void {
    // Fix 3133
    this.notifications.hideToasts();

    // Call unsubscriptions
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public workbookInit(args) {
    this.spread = args.spread;
    this.setupWorkBook();
  }

  private setupWorkBook() {
    this.sheet = this.spread.getActiveSheet();

    suspendAll(this.sheet);

    setDefaultBookOptions(this.spread.options, {
      // Overwrite options here
      allowExtendPasteRange: true,
    });
    setDefaultSheetOptions(this.sheet.options);

    if (this.userRoleCanEdit) {
      // Protection options (after setDefaultSheetOptions)
      this.sheet.options.protectionOptions = {
        // Lock cell options
        allowSelectLockedCells: false,
        allowSelectUnlockedCells: true,
      };
      this.sheet.options.isProtected = false;
      // Context Menu (right-click)
      SpreadContextMenu.setCopyCutPaste(this.spread).enableInsertRowsAboveAndBelow(this.spread).enableDeleteRows(this.spread);
    } else {
      this.sheet.getRange(-1, 0, -1, this.sheet.getColumnCount()).locked(true);
      this.sheet.options.isProtected = true;
      this.sheet.options.protectionOptions.allowSelectLockedCells = true;
      SpreadContextMenu.setCopyCutPaste(this.spread);
    }

    // On cell edit leave, on cell value change, on paste
    this.setSheetEventBindings();

    resumeAll(this.sheet);
  }

  private loadData() {
    this.getAllExogenousLines();
  }

  private getAllExogenousLines(): void {
    this.validSheet = false;

    this.exogService
      .getExogenousLines()
      .pipe(
        takeUntil(this.destroyed$),
        backoff(3, 250),
        catchError(err => of([]))
      )
      .subscribe((newData: ExogenousResponse[]) => {
        this.resetReferences();
        const obj = this.prepareDataFromDatabase(newData);
        this.loadSheet(obj);
        this.isLoading = false;
        this.menu.emitResize();
      });
  }

  private resetReferences() {
    // Reset all the references
    this.deletedExogenousLines = [];
    this.newExogenousLines = [];
    this.storedExogenousLines = [];
    this.editedExogenousLines = [];
  }

  private prepareDataFromDatabase(obj) {
    // Parsing rules for info that comes from the Database
    return obj.map((row, ind) => {
      if (!this.isEmpty(row)) {
        // Enables the GroupID to be number or string
        row['Group_ID'] = row['Group_ID'] === '' ? '' : !isNaN(Number(row['Group_ID'])) ? Number(row['Group_ID']) : row['Group_ID'];

        // --- Fail_Operator ---
        row['Fail_Operator'] =
          row['Fail_Operator'] && this.spreadConfig.EXO_Accepted_Fail_Operator.includes(row['Fail_Operator']) ? row['Fail_Operator'] : '';

        // --- Risk_Limit ---
        row['Risk_Limit'] =
          typeof row['Risk_Limit'] === 'string' && row['Risk_Limit'] !== this.spreadConfig.DEFAULT_RISK_LIMIT_VALUE
            ? row['Risk_Limit']
            : this.spreadConfig.DEFAULT_RISK_LIMIT_VALUE;

        // --- Display_In_Summary ---
        row['Display_In_Summary'] =
          // Default 1 values to 'Yes' in Display In Summary and 0 values to 'No
          row['Display_In_Summary'] === true || row['Display_In_Summary'] === 1
            ? 'Yes'
            : row['Display_In_Summary'] === false || row['Display_In_Summary'] === 0
            ? 'No'
            : row['Display_In_Summary'];
      }
      return row;
    });
  }

  private loadSheet(exogenousLines: ExogenousResponse[]) {
    suspendAll(this.sheet);
    this.sheet.reset();

    if (!this.userRoleCanEdit) {
      this.sheet.getRange(-1, 0, -1, this.sheet.getColumnCount()).locked(true);
      this.sheet.options.isProtected = true;
      this.sheet.options.protectionOptions.allowSelectLockedCells = true;
      SpreadContextMenu.setCopyCutPaste(this.spread);
    }

    if (exogenousLines !== null) {
      this.storedExogenousLines = exogenousLines;

      const nRows = this.storedExogenousLines.length;

      this.sheet.setRowCount(nRows || 5);
      this.data = new Array<ExogenousResponse>(nRows).fill(new ExogenousResponse());

      this.storedExogenousLines.forEach((exogenous, index) => {
        this.addToGroupIDList(exogenous.Group_ID);

        const exog: ExogenousResponse = new ExogenousResponse();
        exog.Account_Number = exogenous.Account_Number;
        exog.Mnemonic = exogenous.Mnemonic;
        exog.Group_ID = exogenous.Group_ID;
        exog.Calculate_Group = exogenous.Calculate_Group;
        exog.Display_Format = exogenous.Display_Format;
        exog.Display_Order = exogenous.Display_Order;
        exog.Description = exogenous.Description;
        exog.Name = exogenous.Name;
        exog.Simulation_Order = exogenous.Simulation_Order;
        exog.Risk_Limit = exogenous.Risk_Limit;
        exog.Fail_Operator = exogenous.Fail_Operator;
        exog.Display_In_Summary = exogenous.Display_In_Summary;
        exog.Exogenous_Sid = exogenous.Exogenous_Sid;

        this.data[index] = exog as ExogenousResponse;
      });
      // AddRows, EmptyRows
      this.addBottomEmptyRows();
    } else {
      this.data[0] = new ExogenousResponse();
    }

    this.setDefaultValues();
    this.sheet.setDataSource(this.data);
    this.setDataBinding();

    this.setFormatters();
    this.isValidSheet();

    this.showData(`Load Sheet`);

    this.rebuildGroupIdList();
    this.checkDataLoss();
    resumeAll(this.sheet);

    this.isLoading = false;
  }

  // Debug helper -- Uncomment for debugging purposes
  private showData(msg: string = '') {
    // console.log(
    //  `🏓 ${msg}: ~data  (New: ${this.newExogenousLines.length}, Edited: ${
    //    this.editedExogenousLines.length
    //  }, Deleted: ${this.deletedExogenousLines.length})`
    // );
    // console.log({
    //  new: this.newExogenousLines,
    //  edited: this.editedExogenousLines,
    //  deleted: this.deletedExogenousLines
    // });
    // console.table(this.data, [`User_ID`, `Exogenous_Sid`, `Mnemonic`, `Name`, `Account_Number`]);
  }

  private setFormatters() {
    this.autofitDescription(-1);
    this.addRiskLimitFormatter();
  }

  private autofitDescription(row: number) {
    if (row < 0) {
      this.data.forEach((s, index) => {
        this.sheet.getCell(index, 4).wordWrap(true);
        this.sheet.autoFitRow(index);
      });
    } else {
      this.sheet.getCell(row, 4).wordWrap(true);
      this.sheet.autoFitRow(row);
    }
  }

  private addRiskLimitFormatter(specificRow = -1) {
    const col = 10;
    // Risk Limit as Double / Decimal with two decimals
    this.sheet.setFormatter(
      specificRow,
      col,
      // Introducing numbers in spreadJS only allows 16 decimals.
      // In the DB is handled as string with a max. of 100 characters
      this.spreadConfig.EXO_Formatters.riskLimit,
      GC.Spread.Sheets.SheetArea.viewport
    );
  }

  private setDefaultValues() {
    this.data.map((row, index) => {
      if (!this.isEmpty(row)) {
        row['Mnemonic'] = row['Mnemonic'] === null || row['Mnemonic'] === undefined ? '' : row['Mnemonic'];

        row['Group_ID'] = row['Group_ID'] === null || row['Group_ID'] === undefined ? '' : row['Group_ID'];

        row['Account_Number'] = row['Account_Number'] === null || row['Account_Number'] === undefined ? 0 : row['Account_Number'];

        row['Description'] = row['Description'] === null || row['Description'] === undefined ? '' : row['Description'];

        row['Display_Format'] = row['Display_Format'] === null || row['Display_Format'] === undefined ? 'stock' : row['Display_Format'];

        row['Calculate_Group'] = row['Calculate_Group'] === null || row['Calculate_Group'] === undefined ? 0 : row['Calculate_Group'];

        row['Simulation_Order'] =
          row['Simulation_Order'] === null || row['Simulation_Order'] === undefined ? 'pre-sim' : row['Simulation_Order'];

        row['Display_Order'] = row['Display_Order'] === null || row['Display_Order'] === undefined ? 0 : row['Display_Order'];

        row['Fail_Operator'] =
          row['Fail_Operator'] === null ||
          row['Fail_Operator'] === undefined ||
          !this.spreadConfig.EXO_Accepted_Fail_Operator.includes(row['Fail_Operator'])
            ? ''
            : row['Fail_Operator'];

        row['Risk_Limit'] =
          row['Risk_Limit'] === null || row['Risk_Limit'] === undefined
            ? this.spreadConfig.DEFAULT_RISK_LIMIT_VALUE
            : `${row['Risk_Limit']}`;

        row['Display_In_Summary'] =
          row['Display_In_Summary'] === null || row['Display_In_Summary'] === undefined ? 'Yes' : row['Display_In_Summary'];
      }
    });
  }

  private setValidations(row: number = null) {
    try {
      suspendAll(this.sheet);
      if (row !== null && row >= 0) {
        this.addValidationsToRow(row);
      } else {
        this.data.forEach((acc, index) => this.addValidationsToRow(index));
      }
      resumeAll(this.sheet);
    } catch {}
  }

  private addValidationsToRow(row: number) {
    if (row >= 0) {
      this.addAccountNameValidatorToRow(row);
      this.addMnemonicValidatorToRow(row);
      this.addAccountNumberValidatorToRow(row);
      this.addCalculateGroupValidatorToRow(row);
      this.addDisplayOrderValidatorToRow(row);
      this.addRiskLimitValidatorToRow(row);
      // Dropdowns
      this.addSimulationOrderValidatorToRow(row);
      this.addDisplayFormatValidatorToRow(row);
      this.addGroupIDValidatorToRow(row);
      this.addFailOperatorValidatorToRow(row);
      this.addDisplayInSummaryValidatorToRow(row);
    }
  }

  addMnemonicValidatorToRow(r: number) {
    let validCell = false;
    const row = r + 1;

    // ================
    //     MNEMONIC
    // ================
    // Row is non-empty, cell is non-empty, and unique in its column
    // Mnemonic filed should only accept letters, numbers, underscores (_). It should not allow spaces.
    // Hyphens are restricted (bug fix 1957)

    const condition = `OR(
     OR(COUNTBLANK(A${row}:I${row})=9, AND(COUNTBLANK(A${row}:G${row})=8),
     AND(
       NOT(ISBLANK(TRIM(B${row}))),
       NOT(OR(ISNUMBER(SEARCH("~?",B${row})), ISNUMBER(SEARCH("~*",B${row})), ISNUMBER(SEARCH("~~",B${row})))),
       COUNTIF(B:B,B${row}) = 1,
       IF(B${row}="",TRUE,IF(ISERROR(SUMPRODUCT(SEARCH(MID(B${row},
         ROW(INDIRECT("1:"&LEN(B${row}))),1),
         "0123456789abcdefghijklmnopqrstuvwxyz_"))),FALSE,TRUE))
     )
   )`;

    const MnemonicValidation = GC.Spread.Sheets.DataValidation.createFormulaValidator(condition);

    this.sheet.setDataValidator(r, COL_MNEMONIC, 1, 1, MnemonicValidation);
    MnemonicValidation.ignoreBlank(false);

    const value = this.sheet.getCell(r, COL_MNEMONIC).value();
    validCell = this.sheet.isValid(r, COL_MNEMONIC, value);
  }

  private addAccountNameValidatorToRow(r: number) {
    let validCell = false;
    // const row = r + 1;
    // Refactored using Liquidity Sources Formula builder to achieve a quick solution
    // TODO: Refactorize validations of multiple spreadsheets to centralize and standarize the way we validate data
    const dv = this.liqSrcService.getNameValidator(r);
    dv.ignoreBlank(false);
    this.sheet.setDataValidator(r, COL_ACCOUNT_NAME, 1, 1, dv);
    validCell = this.sheet.isValid(r, COL_ACCOUNT_NAME, this.sheet.getCell(r, 0).value());
  }

  addCalculateGroupValidatorToRow(r: number) {
    let validCell = false;
    const row = r + 1;
    const value = this.sheet.getCell(r, 6).value();

    // =================
    //   Calculate Group
    // =================
    // Row is empty, or cell is a number >= 0, or cell is empty
    // Spec: vii.	The Display Order should be numeric or blank.
    // If it isn’t it will result in the following validation message: “The display order should be numeric or blank”.
    const DisplayOrderValidation = GC.Spread.Sheets.DataValidation.createFormulaValidator(
      `OR(G${row}=0,AND(OR(AND(ISNUMBER(G${row}),G${row}>=0,MOD(G${row},1)=0),ISBLANK(G${row}))))`
    );

    DisplayOrderValidation.ignoreBlank(false);
    this.sheet.setDataValidator(r, COL_CALCULATE_GRP, 1, 1, DisplayOrderValidation); // Display Order

    validCell = this.sheet.isValid(r, COL_CALCULATE_GRP, value);

    // Displaying % sign
    // this.sheet.getCell(r, 5).formula(`=(.01* ${value})`);
    // this.sheet.getCell(r, 5).formatter('0 %');
  }

  addDisplayOrderValidatorToRow(r: number) {
    let validCell = false;
    const row = r + 1;
    const value = this.sheet.getCell(r, 5).value();

    // =================
    //   DISPLAY ORDER
    // =================
    // Row is empty, or cell is a number >= 0, or cell is empty
    // Spec: vii.	The Display Order should be numeric or blank.
    // If it isn’t it will result in the following validation message: “The display order should be numeric or blank”.
    const DisplayOrderValidation = GC.Spread.Sheets.DataValidation.createFormulaValidator(
      `OR(I${row}=0,AND(OR(AND(ISNUMBER(I${row}),I${row}>=0),ISBLANK(I${row}))))`
    );

    DisplayOrderValidation.ignoreBlank(false);
    this.sheet.setDataValidator(r, COL_DISPLAY_ORDER, 1, 1, DisplayOrderValidation); // Display Order

    validCell = this.sheet.isValid(r, COL_DISPLAY_ORDER, value);
  }

  private addFailOperatorValidatorToRow(r: number) {
    // =================
    //   FAIL OPERATOR
    // =================
    // Should be string containing one of the values allowed
    let validCell = false;

    const value = this.sheet.getCell(r, COL_FAIL_OPERATOR).value();
    const dv = GC.Spread.Sheets.DataValidation.createListValidator(this.spreadConfig.EXO_Accepted_Fail_Operator.join(','));
    dv.ignoreBlank(true);
    dv.inCellDropdown(true);
    dv.showInputMessage(false);
    this.sheet.setDataValidator(r, COL_FAIL_OPERATOR, 1, 1, dv); // Fail Operator
    validCell = this.sheet.isValid(r, COL_FAIL_OPERATOR, value);

    // Deprecated 17-APR-2019
    // Validator Formula that handles the field as free string
    // let validCell = false;
    // const col = 9;
    // const value = this.sheet.getCell(r, col).value();
    // const row = r + 1;
    // const colLetter = 'J';
    // const formulaElements = [];
    // for (let i = 0; i < this.spreadConfig.EXO_Accepted_Fail_Operator.length; i++) {
    //   formulaElements.push(`${colLetter}${row}="${this.spreadConfig.EXO_Accepted_Fail_Operator[i]}"`);
    // }
    // const dv = GC.Spread.Sheets.DataValidation.createFormulaValidator(`OR(${formulaElements.join(', ')})`);
    // dv.ignoreBlank(false);
    // this.sheet.setDataValidator(r, col, 1, 1, dv); // Fail Operator
    // validCell = this.sheet.isValid(r, col, value);
  }

  private addRiskLimitValidatorToRow(r: number) {
    let validCell = false;

    const row = r + 1;
    const colLetter = 'K';
    const value = this.sheet.getCell(r, COL_RISK_LIMIT).value();

    // =================
    //   RISK LIMIT
    // =================
    const hasOnlyAcceptedCharacters = (charactersAllowed, columnLetter, sheetRowNum) => `(
      IF(
        TRIM(${columnLetter}${sheetRowNum})="",
        TRUE,
        IF( NOT(ISERROR(SUMPRODUCT(SEARCH(MID(${columnLetter}${sheetRowNum},
          ROW(INDIRECT("1:"&LEN(${columnLetter}${sheetRowNum}))),1),
          "${charactersAllowed}")))),
          TRUE,
          FALSE
        )
      )
    )`;
    const dv = GC.Spread.Sheets.DataValidation.createFormulaValidator(`OR(${hasOnlyAcceptedCharacters('0123456789.,-+', colLetter, row)})`);
    dv.ignoreBlank(true);
    this.sheet.setDataValidator(r, COL_RISK_LIMIT, 1, 1, dv); // Risk Limit
    validCell = this.sheet.isValid(r, COL_RISK_LIMIT, value);
  }

  addAccountNumberValidatorToRow(r: number) {
    let validCell = false;
    const row = r + 1;
    // ==================
    //   ACCOUNT NUMBER
    // ==================
    //
    // Row is empty, OR
    //   Cell is a number >= 0 AND either
    //   -> Unique in its column OR
    //   -> has Filter Criteria AND has no children
    //
    // OR(
    //   COUNTBLANK(A1:G1) = 7,         // row is empty
    //   AND(
    //     ISNUMBER(D1),D1> = 0, 			  // number >= 0
    //         OR(
    //           COUNTIF(D:D,D1) = 1,		// unique
    //           AND(
    //             NOT(ISBLANK(G1)),	  // has filter criteria
    //             COUNTIF(E:E,D1) = 0  // has no children
    //           )
    //         )
    //     )
    // )

    const condition = `OR(
     OR(COUNTBLANK(A${row}:I${row})=9, AND(COUNTBLANK(A${row}:G${row})=8),
     AND(
       ISNUMBER(D${row}),D${row}>=0,
       OR(
         COUNTIF(D:D,D${row})=1
       )
     )
   )`;

    const accNumValidator = GC.Spread.Sheets.DataValidation.createFormulaValidator(condition);

    this.sheet.setDataValidator(r, COL_ACCOUNT_NUM, 1, 1, accNumValidator);
    accNumValidator.ignoreBlank(false);

    const value = this.sheet.getCell(r, COL_ACCOUNT_NUM).value();
    validCell = this.sheet.isValid(r, COL_ACCOUNT_NUM, value);
    accNumValidator.showInputMessage(!validCell);
  }

  get validationFeedback() {
    return this.invalidRows.length > 0 ? `Validation errors in rows\n${this.invalidRows.toString()}` : '';
  }

  private isItemInGroupIDList(val: string) {
    if (this.groupIDList.map(g => `${g}`.toLowerCase()).includes(`${val}`.toLowerCase())) {
      return true;
    }
    return false;
  }

  private addToGroupIDList(val: any) {
    if (!isEmpty(val) && !this.isItemInGroupIDList(val)) {
      this.groupIDList = [...this.groupIDList, `${val}`];
    }
  }

  private setDataBinding() {
    this.sheet.setColumnCount(this.spreadConfig.columnsConfig.length);
    this.sheet.bindColumns(this.spreadConfig.columnsConfig);
  }

  private addSimulationOrderValidatorToRow(r: number) {
    const list = this.spreadConfig.EXO_Accepted_Simulation_Order; // Non Editable
    const dv = GC.Spread.Sheets.DataValidation.createListValidator(list.join(','));
    dv.ignoreBlank(true);
    dv.inCellDropdown(true);
    dv.showInputMessage(false);
    this.sheet.setDataValidator(r, COL_SIMULATION_ORDER, 1, 1, dv);
  }

  private addDisplayFormatValidatorToRow(r: number) {
    const list = this.spreadConfig.EXO_Accepted_Display_Format; // Non Editable
    const dv = GC.Spread.Sheets.DataValidation.createListValidator(list.join(','));
    dv.ignoreBlank(true);
    dv.inCellDropdown(true);
    dv.showInputMessage(false);
    this.sheet.setDataValidator(r, COL_DISPLAY_FORMAT, 1, 1, dv);
  }

  private addDisplayInSummaryValidatorToRow(r: number) {
    const list = this.spreadConfig.EXO_Accepted_Display_In_Summary; // Non Editable
    const dv = GC.Spread.Sheets.DataValidation.createListValidator(list.join(','));
    dv.ignoreBlank(true);
    dv.inCellDropdown(true);
    dv.showInputMessage(false);
    this.sheet.setDataValidator(r, COL_DISPLAY_IN_SUMMARY, 1, 1, dv);
  }

  private addGroupIDValidatorToRow(r: number) {
    const dv = GC.Spread.Sheets.DataValidation.createListValidator(
      // Editable thanks to the method handleGroupIDInput()
      this.groupIDList.join(',')
    );
    dv.ignoreBlank(true);
    dv.inCellDropdown(true);
    dv.showInputMessage(false);
    this.sheet.setDataValidator(r, COL_GROUP_ID, 1, 1, dv);
  }

  /**
   * Encapsulation-helper function
   */
  private reCheck(changeSource: string = null) {
    this.setValidations();
    this.isValidSheet();
    this.addBottomEmptyRows();
    this.updateSaveButton(changeSource);
  }

  /* // ! WARNING
    Removing the async affects seriously the save button enable/disable behavior
    for complex operations. It pospones the execution until the events has ended
  */
  private async updateSaveButton(changeSource): Promise<void> {
    if (this.userRoleCanEdit) {
      const page = 'exogenous-lines';
      this.menu.dialogSaveButton = {
        ...this.menu.dialogSaveButton,
        page,
        enabled: changeSource === 'save' ? false : await this.saveEnabled(),
        clicked: changeSource === 'save' ? true : false,
        valid:
          changeSource === 'save'
            ? // Forzes reset of all the validations
              true
            : this.validSheet,
        source: `${page} ${changeSource || 'reCheck'}`,
      };
    }
  }

  /*
  =================
  EVENTS
  =================
  */

  private setSheetEventBindings() {
    bindSpreadJSEventsToSheet(this.sheet);
    mapActionKeys(this.spread, this.sheet, null, this.userRoleCanEdit);

    this.setRowChangedEvent();
    this.setValueChangedEvent();
    this.setValidationErrorEvent();
    this.setDragFillBlockCompletedEvent();
    this.setDragDropBlockCompletedEvent();
    this.addDeleteKeyBinding();
    this.setClipboardPastingEvent(); // Before the pasting event ends
    this.setClipboardPastedEvent(); // After the pasting event ends
  }

  private ifDeletedExog(storedExog: ExogenousResponse) {
    // ! if clause introduced as fix for related Defect: 4090
    if (storedExog) {
      const currentExog = this.data.find(exog => exog.Exogenous_Sid === storedExog.Exogenous_Sid);
      const isDeletedRow =
        // Avoid repetition if already has been tagged for deletion
        !this.deletedExogenousLines.some(delExog => delExog.Exogenous_Sid === storedExog.Exogenous_Sid) &&
        // Use case: If the user has deleted all the fields of the row. It should be pushed to this.deletedExgeonousLines too
        ((currentExog && this.isEmptyExog(currentExog)) ||
          // Use case: User has click in the row and right-click deleted it
          (!this.isBlankOrInvalidExog(storedExog) && storedExog.Exogenous_Sid > -1));

      if (isDeletedRow) {
        this.deletedExogenousLines.push(storedExog);
        // console.log("tagged for deletion", storedExog);
      }
    }
  }

  private setRowChangedEvent() {
    // Event when a row is inserted or deleted - on insert
    this.sheet.bind(GC.Spread.Sheets.Events.RowChanged, (e, info) => {
      suspendAll(this.sheet);

      const { row, count, propertyName: type } = info;
      if (type === 'deleteRows') {
        for (let rowIndex = row; rowIndex < row + count; rowIndex++) {
          if (rowIndex > -1) {
            const exog: ExogenousResponse = this.storedExogenousLines[rowIndex];
            this.ifDeletedExog(exog);
            this.rebuildGroupIdList();
          }
        }
        this.checkDataLoss();
        this.showData(`Deleted row(s)`);
      } else if (type === 'addRows') {
        this.autofitDescription(info.row);
        this.setValidations(info.row);
        this.showData(`Inserted row(s)`);
      }

      if (this.isValidSheet()) {
        this.handlePastedData();
      }
      resumeAll(this.sheet);

      this.reCheck(type);
    });
  }

  private setValueChangedEvent() {
    // Event when user edits a cell modifying his value
    this.sheet.bind(GC.Spread.Sheets.Events.ValueChanged, this.valueChangedCallback);
  }

  private setValidationErrorEvent() {
    // Event on validation error
    this.sheet.bind(GC.Spread.Sheets.Events.ValidationError, (e, info) => {
      // On Validation Error disable save button
      this.hasInvalidations = true;
    });
  }

  private setClipboardPastingEvent() {
    // Trigger on pasting data
    this.sheet.bind(GC.Spread.Sheets.Events.ClipboardPasting, (event, info) => {
      if (this.userRoleCanEdit) {
        this.showData(`ClipboardPasting Start`);

        // Fix 3879: The paste should fail if we are pasting more columns than allowed
        SheetFunctions.preventPastingColumnsBeyond(this.sheet, event, info);
        SheetFunctions.allowPastingRowsBeyond(this.sheet, info);

        // if is a cut event, it clears the clipboard content once the event ends
        clipboardClearOnCut(info, this.sheet);

        const isCutAndPasteEvent = info.pasteOption === 0;
        const isCopyAndPasteEvent = info.pasteOption === 1;
        // console.log(
        //   `It is ${
        //     isCopyAndPasteEvent ? 'a copy-and-paste' : isCutAndPasteEvent ? 'a cut-and-paste' : 'an INVALID'
        //   } paste event`
        // );

        if (isCutAndPasteEvent) {
          // Get Exogenous_Sid of the cut row for deletion
          const cutRowsFound = this.storedExogenousLines.filter(exog => this.isEmpty(exog));
          const cutSids = cutRowsFound.map(exog => exog.Exogenous_Sid);
          if (cutSids.length > 0) {
            for (let i = 0; i < cutSids.length; i++) {
              const toDeleteExog = new ExogenousResponse();
              toDeleteExog['Exogenous_Sid'] = cutSids[i];
              toDeleteExog['User_ID'] = -1;
              // cut rows will be deleted but the server will only receive the Exogenous_Sid
              if (toDeleteExog['Exogenous_Sid'] && toDeleteExog['User_ID']) {
                this.deletedExogenousLines.push(toDeleteExog);
              }
            }
            // Clean stored ExogenousLines
            this.storedExogenousLines = this.storedExogenousLines.filter(exog => !this.isEmpty(exog));
          }
        }

        // Look for the empty rows and make them default
        this.zone.run(() => {
          this.data.map((_row, index) => {
            const isBlankCondition =
              this.isEmptyRow(index) ||
              JSON.stringify(_row) === '{}' ||
              (!_row['Account_Number'] &&
                !_row['Calculate_Group'] &&
                !_row['Description'] &&
                !_row['Display_Format'] &&
                !_row['Display_Order'] &&
                !_row['Equations'] &&
                !_row['Group_ID'] &&
                !_row['Mnemonic'] &&
                !_row['Name'] &&
                !_row['Simulation_Order'] &&
                (!_row['Fail_Operator'] || _row['Fail_Operator'] !== '') &&
                (!_row['Risk_Limit'] || _row['Risk_Limit'] !== this.spreadConfig.DEFAULT_RISK_LIMIT_VALUE) &&
                (!_row['Display_In_Summary'] || _row['Display_In_Summary'] !== 'Yes'));

            if (isBlankCondition) {
              _row = new ExogenousResponse();
            }
            return _row;
          });

          const sheetRowCount = this.sheet.getRowCount();
          const { row, rowCount } = info.cellRange;
          const newRowsNeeded = rowCount - (sheetRowCount - row) + EMPTY_BOTTOM_ROWS;
          // console.log('need new row', newRowsNeeded);
          if (newRowsNeeded > 0 && info.ColCount > 1) {
            this.sheet.addRows(sheetRowCount, newRowsNeeded);
            for (let i = 0; i < newRowsNeeded; i++) {
              const existingExogenousSid = this.data[row + i]['Exogenous_Sid'];
              const newobj = new ExogenousResponse();
              this.data[row + i] = {
                ...newobj,
                User_ID: -1,
                Exogenous_Sid: existingExogenousSid || newobj.Exogenous_Sid,
              };
            }
          } else {
            this.sheet.addRows(sheetRowCount, newRowsNeeded);
          }
        });

        this.showData(`ClipboardPasting End`);
      }
    });
  }

  private setClipboardPastedEvent() {
    // Copy-paste. Trigger on pasted data
    this.sheet.bind(GC.Spread.Sheets.Events.ClipboardPasted, (event, info) => {
      if (this.userRoleCanEdit) {
        this.showData(`ClipboardPasted Start`);
        this.data.forEach((exog: ExogenousResponse) => this.addToGroupIDList(exog.Group_ID));

        this.setValidations();

        this.handlePastedData();

        // Clean empty string values from pasted data
        this.cleanEmptyStringValues();

        this.reCheck(`ClipboardPasted`);
        this.showData(`ClipboardPasted End`);
      }
    });
  }

  /**
   * Convert whitespace-only string values to null to prevent validation errors
   * This fixes the issue where empty cells are pasted as empty strings instead of null
   */
  private cleanEmptyStringValues() {
    this.data.forEach((exog: ExogenousResponse) => {
      // Clean string fields that should be null when empty
      if (exog.Name && typeof exog.Name === 'string' && exog.Name.trim() === '') {
        exog.Name = null;
      }
      if (exog.Mnemonic && typeof exog.Mnemonic === 'string' && exog.Mnemonic.trim() === '') {
        exog.Mnemonic = null;
      }
      if (exog.Description && typeof exog.Description === 'string' && exog.Description.trim() === '') {
        exog.Description = null;
      }
      if (exog.Group_ID && typeof exog.Group_ID === 'string' && exog.Group_ID.trim() === '') {
        exog.Group_ID = null;
      }
      if (exog.Fail_Operator && typeof exog.Fail_Operator === 'string' && exog.Fail_Operator.trim() === '') {
        exog.Fail_Operator = null;
      }
      if (exog.Risk_Limit && typeof exog.Risk_Limit === 'string' && exog.Risk_Limit.trim() === '') {
        exog.Risk_Limit = null;
      }
      if (exog.Display_In_Summary && typeof exog.Display_In_Summary === 'string' && exog.Display_In_Summary.trim() === '') {
        exog.Display_In_Summary = null;
      }
    });
  }

  private setDragDropBlockCompletedEvent() {
    this.sheet.bind(GC.Spread.Sheets.Events.DragDropBlockCompleted, (event, info) => {
      this.reCheck('DragDropBlockCompleted');
    });
  }

  private setDragFillBlockCompletedEvent() {
    this.sheet.bind(GC.Spread.Sheets.Events.DragFillBlockCompleted, (event, info) => {
      this.reCheck('DragFillBlockCompleted');
    });
  }

  // Wrapper Helper Function
  private valueChangedCallback = (e, info) => {
    if (!this.isDeletionInProgress) {
      suspendAll(this.sheet);
      const exog: ExogenousResponse = Object.assign(new ExogenousResponse(), this.data[info.row]);

      this.isEditing = true;
      const isAccNumValid =
        !this.isDeletionInProgress &&
        // has some value in account number
        this.sheet.getCell(info.row, COL_ACCOUNT_NAME).value() !== null &&
        // has mnemonic too
        this.sheet.getCell(info.row, COL_MNEMONIC).value() !== null &&
        // is valid
        this.sheet.isValid(info.row, COL_ACCOUNT_NAME, this.sheet.getCell(info.row, COL_ACCOUNT_NAME).value());

      // Group_ID
      // -- Make possible to include new information in the Dropdowns
      if (info.col === COL_GROUP_ID) {
        const val = checkDropdownValues(this.sheet, info.row, info.col, info.newValue) || null;
        info.newValue = val;
        this.sheet.getCell(info.row, info.col).value(val);
        this.rebuildGroupIdList();
      }

      // Account Number
      // -- Add default values to other fields as validation occurs for the account number
      if (isAccNumValid) {
        if (info.col === COL_ACCOUNT_NUM) {
          // Set default values for some fields after validation
          const afterValidationFilledValues = {
            failOperator: this.sheet.getCell(info.row, COL_FAIL_OPERATOR).value() || '',
            riskLimit: this.sheet.getCell(info.row, COL_RISK_LIMIT).value() || this.spreadConfig.DEFAULT_RISK_LIMIT_VALUE,
            displayInSummary: this.sheet.getCell(info.row, COL_DISPLAY_IN_SUMMARY).value() || 'Yes', // Default value
          };
          this.sheet.getCell(info.row, COL_FAIL_OPERATOR).value(afterValidationFilledValues.failOperator);
          this.sheet.getCell(info.row, COL_RISK_LIMIT).value(afterValidationFilledValues.riskLimit);
          this.sheet.getCell(info.row, COL_DISPLAY_IN_SUMMARY).value(afterValidationFilledValues.displayInSummary);
        }

        if (info.col === COL_DISPLAY_FORMAT) {
          // Make the dropdown case insensitive
          if (info.newValue && typeof info.newValue === 'string') {
            this.data[info.row].Display_Format = info.newValue.toLowerCase().trim();
          }
        }

        if (info.col === COL_SIMULATION_ORDER) {
          // Make the dropdown case insensitive
          if (info.newValue && typeof info.newValue === 'string') {
            this.data[info.row].Simulation_Order = info.newValue.toLowerCase().trim();
          }
        }

        // Fail_Operator
        // -- Convert few invalid values for Fail_Operator into his default value (empty string)
        if (info.col === COL_FAIL_OPERATOR) {
          // On Delete
          if (info.newValue === null || info.newValue === undefined) {
            // -- setTimeout applied to delay the execution until the event ends
            setTimeout(() => this.sheet.getCell(info.row, info.col).value(''));
          } else {
            // Enforze string type
            setTimeout(() => this.sheet.getCell(info.row, info.col).value(`${info.newValue}`));
          }
        }

        // Risk_Limit
        // -- Convert not numeric values to his default value (0)
        if (info.col === COL_RISK_LIMIT) {
          // On Delete
          if (info.newValue !== null && info.newValue !== undefined) {
            // Invalid values are autoconverted to the default value
            if (!this.sheet.isValid(info.row, info.col, this.sheet.getCell(info.row, info.col).value())) {
              this.sheet.getCell(info.row, info.col).value(this.spreadConfig.DEFAULT_RISK_LIMIT_VALUE);
            } else {
              // Enforce string conversion
              this.data[info.row]['Risk_Limit'] = `${this.data[info.row]['Risk_Limit']}`;
            }
          }
        }

        // Display_In_Summary
        if (info.col === COL_DISPLAY_IN_SUMMARY) {
          if (info.newValue === null || info.newValue === undefined) {
            // Added because is a mandatory field
            setTimeout(() => this.sheet.getCell(info.row, info.col).value('Yes'));
          } else {
            // Autoconversion helpers 1 and y* ==> 'Yes'. 0 and n* or null ==> No
            const lowerCased = `${this.data[info.row]['Display_In_Summary']}`.toLowerCase().trim();
            if (
              // "Yes" conditions
              lowerCased === '1' ||
              (lowerCased.length > 0 && lowerCased[0] === 'y')
            ) {
              this.data[info.row]['Display_In_Summary'] = 'Yes';
            } else if (
              // "No" conditions
              lowerCased === '0' ||
              (lowerCased.length > 0 && lowerCased[0] === 'n')
            ) {
              this.data[info.row]['Display_In_Summary'] = 'No';
            }
            // Invalid conditions
            // else {
            //   this.data[info.row]['Display_In_Summary'] = capitalizeString(
            //     `${this.data[info.row]['Display_In_Summary']}`
            //   );
            // }
          }
        }
      }

      if (this.isValidSheet()) {
        const _exog: ExogenousResponse = <ExogenousResponse>this.data[info.row]; // this.storedExogenousLines[info.row];

        const newInsertion = () => {
          // New Insertion
          // console.log('New Add');
          const temp = <ExogenousResponse>{
            Exogenous_Sid: -1,
            Name: clone(this.data[info.row]).Name,
            Account_Number: clone(this.data[info.row]).Account_Number,
            Display_Format: clone(this.data[info.row]).Display_Format,
            Display_Order: clone(this.data[info.row]).Display_Order,
            Mnemonic: clone(this.data[info.row]).Mnemonic,
            Simulation_Order: clone(this.data[info.row]).Simulation_Order,
            User_ID: -1,
            Group_ID: clone(this.data[info.row]).Group_ID,
            Calculate_Group: clone(this.data[info.row]).Calculate_Group,
            Description: clone(this.data[info.row]).Description,
            Fail_Operator: clone(this.data[info.row]).Fail_Operator,
            Risk_Limit: clone(this.data[info.row]).Risk_Limit,
            Display_In_Summary: clone(this.data[info.row]).Display_In_Summary,
          };

          this.addExogenousLine(temp);
        };

        if (_exog['Name'] && _exog['Mnemonic'] && _exog['Account_Number']) {
          if (_exog['Exogenous_Sid']) {
            _exog.User_ID = -1;
            // console.log('edited Exog', exog);
            this.editedExogenousLines.push(_exog);
          } else {
            // Bug fix 2659
            // Problem: New insertions has all the same "-1" Exogenous_Sid.
            // Solution: Every time a cell changes, look for the created row and modify his value
            if (!this.data[info.row]['Exogenous_Sid']) {
              // TODO: Defect -> Category mapping does not match info.col return value, making the data to be
              // TODO: placed in the wrong column
              console.error('Error mapping Exogenous Lines. See comment in exogenous-lines.component.ts');
              const colName = this.categoryMapping[info.col];
              const foundRow = this.newExogenousLines.find(row => row[colName] === info.oldValue) || false;

              if (!foundRow) {
                newInsertion();
              } else {
                // Update the value in the array newExogenousLines from the older to the newer
                this.newExogenousLines = this.newExogenousLines.map(row => {
                  if (row[colName] === info.oldValue) {
                    row[colName] = info.newValue;
                  }
                  return row;
                });
              }
            } else {
              newInsertion();
            }
          }
        }
        // Else is blank. Ignore it

        this.isEditing = false;
      }

      this.removeUnnecessaryGroupIds();
      resumeAll(this.sheet);
      this.reCheck('valueChangedCallback');
    }
  };

  // Check have we pasted over data in teh table
  // if so then we have cut sids out so we need to handle this.
  private handlePastedData() {
    const datTemp = this.data;
    datTemp.forEach((data, index) => {
      if (this.checkForCutPastedNullRecords(data)) {
        this.data.splice(index, 1);
      }
    });
    this.cleanupData();
  }

  private cleanupData() {
    this.data.forEach((ele, index) => this.checkForStoredRecordMatch(ele));
  }

  private checkForStoredRecordMatch(exog: ExogenousResponse) {
    this.storedExogenousLines.forEach((ele, index) => {
      if (this.isExistingExogenous(exog, ele) && ele.Exogenous_Sid !== exog.Exogenous_Sid && exog.Exogenous_Sid > 0) {
        exog.Exogenous_Sid = ele.Exogenous_Sid;
      }
    });
  }

  private isExistingExogenous(exog1: ExogenousResponse, exog2: ExogenousResponse): boolean {
    return (
      exog1.Account_Number === exog2.Account_Number &&
      exog1.Name === exog2.Name &&
      exog1.Group_ID === exog2.Group_ID &&
      exog1.Calculate_Group === exog2.Calculate_Group &&
      exog1.Mnemonic === exog2.Mnemonic &&
      exog1.Description === exog2.Description &&
      exog1.Display_Format === exog2.Display_Format &&
      exog1.Display_Order === exog2.Display_Order &&
      exog1.Simulation_Order === exog2.Simulation_Order &&
      exog1.Risk_Limit === exog2.Risk_Limit &&
      exog1.Fail_Operator === exog2.Fail_Operator &&
      exog1.Display_In_Summary === exog2.Display_In_Summary
    );
  }

  // Delete key binding workaround
  private addDeleteKeyBinding() {
    const actionOnDeletion = () => {
      this.isDeletionInProgress = true;
      const activeSheet = this.spread.getActiveSheet();
      const selectionRange = activeSheet.getSelections()[0];

      // Call the callback on value changed after the end of the event (to avoid unnecessary calls)
      const info = {
        sheet: activeSheet,
        sheetName: activeSheet.name(),
        row: selectionRange.row + selectionRange.rowCount - 1,
        col: selectionRange.col + selectionRange.colCount - 1,
        newValue: null,
      };
      info['oldValue'] = this.sheet.getCell(info.row, info.col).value();

      // ! ALERT: modifying the next lines affects to editedExogenousLines array
      // Iterate through each cell changed from left to right and from top to bottom:
      // For each row changed
      suspendAll(this.sheet);
      for (let y = 0; y < selectionRange.rowCount; y++) {
        const row = selectionRange.row + y;
        // For each column changed in that row

        for (let x = 0; x < selectionRange.colCount; x++) {
          const col = selectionRange.col + x;
          const exog: ExogenousResponse = this.storedExogenousLines.find(stExog => stExog.Exogenous_Sid === this.data[row].Exogenous_Sid);

          if (exog) this.ifDeletedExog(exog);

          const paramsObject = {
            sheet: activeSheet,
            sheetName: activeSheet.name(),
            row,
            col,
            oldValue: this.sheet.getCell(row, col).value(),
            newValue: null,
          };

          const isLastIteration = x === selectionRange.colCount - 1 && y === selectionRange.rowCount - 1;
          if (isLastIteration) this.isDeletionInProgress = false;

          // ! valueChangedCallback acts differently depending of this.isDeletionInProgress value
          this.valueChangedCallback({ type: 'ValueChanged' }, paramsObject);
        }
      }
      resumeAll(this.sheet);
      this.checkDataLoss();
    };

    const deleteCommand = {
      canUndo: true,
      execute: function (context, options, isUndo) {
        const Commands = GC.Spread.Sheets.Commands;
        if (isUndo) {
          // Undo feature for 'Delete' key only
          Commands.undoTransaction(context, options);
          return true;
        } else {
          Commands.startTransaction(context, options);
          actionOnDeletion();
          Commands.endTransaction(context, options);
          return false;
        }
      },
    };

    const commandManager = this.spread.commandManager();
    if (this.userRoleCanEdit) {
      commandManager.register('deleteKey', deleteCommand, GC.Spread.Commands.Key.del);
    }
  }

  private addExogenousLine(temp: ExogenousResponse) {
    let exists = false;
    const i = -1;
    this.newExogenousLines.forEach((val, index) => {
      if (this.ExogenousMatch(val, temp)) {
        exists = true;
        val.Description = temp.Description;
        val.Display_Format = temp.Display_Format;
        val.Display_Order = temp.Display_Order;
        val.Calculate_Group = temp.Calculate_Group;
        val.Simulation_Order = temp.Simulation_Order;
        val.Group_ID = temp.Group_ID;
        val.Fail_Operator = temp.Fail_Operator;
        val.Risk_Limit = temp.Risk_Limit;
        val.Display_In_Summary = temp.Display_In_Summary;
      }
    });
    if (!exists) {
      this.newExogenousLines.push(temp);
      this.addNewRow();
    }
  }

  private addNewRow() {
    let emptyRows = 0;
    this.data.forEach((item, index) => {
      if (this.isEmptyRow(index)) {
        emptyRows++;
      }
    });

    if (emptyRows !== EMPTY_BOTTOM_ROWS) {
      this.sheet.addRows(this.sheet.getRowCount(), 1);
      this.addValidationsToRow(this.sheet.getRowCount());
    }
  }

  private ExogenousMatch(add: ExogenousResponse, newAdd: ExogenousResponse): boolean {
    return add.Account_Number === newAdd.Account_Number && add.Name == newAdd.Name && add.Mnemonic == newAdd.Mnemonic;
  }

  private rebuildGroupIdList() {
    // Do not remove the timeout. Is there to achieve the functionality wanted
    setTimeout(() => {
      this.groupIDList = [];
      this.data.forEach((exog, index) => this.addToGroupIDList(exog.Group_ID));
      this.setValidations();
    });
  }

  /** Remove the GroupIDs not being used from the list if there isn't being used */
  private removeUnnecessaryGroupIds() {
    this.groupIDList = [];
    this.data.forEach((exog, index) => this.addToGroupIDList(exog.Group_ID));
    this.setValidations();
  }

  private addBottomEmptyRows() {
    const paddingRows = this.sheet.getRowCount() > 50 ? EMPTY_BOTTOM_ROWS : MINIMUM_ROWS - this.sheet.getRowCount();
    for (let i = 0; i < paddingRows; i++) {
      const exog = new ExogenousResponse();
      exog.User_ID = -1;
      this.data.push(exog);
    }
  }

  private isEmptyRow(r: number) {
    const exog = this.data[r];
    return this.isEmptyExog(exog);
  }

  private isEmpty(obj: ExogenousLine) {
    if (obj) {
      if (!obj.Name && !obj.Mnemonic && !obj.Account_Number) {
        obj = new ExogenousLine();
      }
      // Check If is Empty
      for (const key in obj) {
        if (obj.hasOwnProperty(key) && obj[key] !== null) {
          return false;
        }
      }
    }
    return true;
  }

  private isEmptyExog(exog: ExogenousResponse) {
    const result = isEmpty(exog.Mnemonic) || isEmpty(exog.Name) || isEmpty(exog.Account_Number);
    return result;
  }

  private prepareDataForDatabase(data: ExogenousResponse[]): ExogenousResponse[] {
    const preparedData: ExogenousResponse[] = clone(data).map((row: ExogenousResponse) => {
      // Convert null values to default valuesrow['Name'] = row['Name'] === null || row['Name'] === undefined ? '' : row['Name'];

      row.Name = row.Name === null || row.Name === undefined ? '' : row.Name.toString();
      row.Mnemonic = row.Mnemonic === null || row.Mnemonic === undefined ? '' : row.Mnemonic.toString();

      row.Group_ID = row.Group_ID === null || row.Group_ID === undefined ? '' : row.Group_ID.toString();

      row['Account_Number'] = row['Account_Number'] === null || row['Account_Number'] === undefined ? 0 : row['Account_Number'];

      row['Description'] = row['Description'] === null || row['Description'] === undefined ? '' : row['Description'];

      row['Display_Format'] = row['Display_Format'] === null || row['Display_Format'] === undefined ? 'stock' : row['Display_Format'];

      row['Calculate_Group'] = row['Calculate_Group'] === null || row['Calculate_Group'] === undefined ? 0 : row['Calculate_Group'];

      row['Simulation_Order'] =
        row['Simulation_Order'] === null || row['Simulation_Order'] === undefined ? 'pre-sim' : row['Simulation_Order'];

      row['Display_Order'] = row['Display_Order'] === null || row['Display_Order'] === undefined ? 0 : row['Display_Order'];

      row['Fail_Operator'] =
        row['Fail_Operator'] === null ||
        row['Fail_Operator'] === undefined ||
        !this.spreadConfig.EXO_Accepted_Fail_Operator.includes(row['Fail_Operator'])
          ? ''
          : row['Fail_Operator'];

      row['Risk_Limit'] =
        row['Risk_Limit'] === null || row['Risk_Limit'] === undefined ? this.spreadConfig.DEFAULT_RISK_LIMIT_VALUE : `${row['Risk_Limit']}`;

      // Convert 'Yes' or No' to true or false for the back-end
      row.Display_In_Summary = `${row.Display_In_Summary}` === 'Yes' ? true : false;

      // Overwrite the user id (just in case It is needed)
      row.User_ID = row.User_ID || -1;

      return row;
    });
    return preparedData;
  }

  public saveData() {
    this.isSaving = true;
    const observables: Observable<any>[] = [];

    this.checkDataForSave();

    // DELETIONS
    // ----------
    if (this.deletedExogenousLines.length > 0) {
      // console.log('Exogenous Deletion', this.editedExogenousLines);
      this.deletedExogenousLines = this.prepareDataForDatabase(this.deletedExogenousLines);
      observables.push(this.exogService.deleteExogenousLines(this.deletedExogenousLines));
    }

    // UPDATES
    // ----------
    if (this.editedExogenousLines.length > 0) {
      // console.log('Exogenous Edit', this.editedExogenousLines);
      this.editedExogenousLines = this.prepareDataForDatabase(this.editedExogenousLines);
      observables.push(this.exogService.updateExogenousLines(this.editedExogenousLines));
    }

    // INSERTIONS
    // ----------
    if (this.newExogenousLines.length > 0) {
      // console.log("Exogenous Add", this.newExogenousLines);
      this.newExogenousLines = this.prepareDataForDatabase(this.newExogenousLines);
      observables.push(this.exogService.addExogenousLines(this.newExogenousLines));
    }

    forkJoin(observables)
      .pipe(debounceTime(50), backoff(3, 100))
      .toPromise()
      .then(
        response => {
          if (response) {
            const savedItems = response && response[0];
            this.newExogenousLines = [];
            this.editedExogenousLines = [];
            this.deletedExogenousLines = [];
            // Save confirmation or fail messages
            const success = savedItems.length === savedItems.filter(item => item.success).length;
            if (success) {
              this.showSuccessToast();
            } else {
              this.showFailToast();
              console.warn('Error saving', {
                savedItems,
                response,
              });
            }
          } else {
            this.showFailToast();
            console.warn('Error saving', {
              response,
            });
          }
          this.getAllExogenousLines();
          this.isSaving = false;
        },
        err => console.log(err)
      );
  }

  private checkDataForSave() {
    this.newExogenousLines = [];
    this.editedExogenousLines = [];
    this.deletedExogenousLines = [];

    this.storedExogenousLines.forEach((ele, index) => {
      if (!this.exogenousInTableData(ele)) {
        this.deletedExogenousLines.push(ele);
      }
    });

    this.data
      .filter(s => !this.isEmptyExog(s))
      .forEach((ele, index) => {
        if (!this.isEmptyExog(ele) && ele.Exogenous_Sid < 0) {
          ele.User_ID = -1;
          this.newExogenousLines.push(ele);
        }
      });

    this.data
      .filter(s => !this.isEmptyExog(s) && s.Exogenous_Sid > 0)
      .forEach((ele, index) => {
        if (this.checkStoredRecordsForChanges(ele)) {
          ele.User_ID = -1;
          this.editedExogenousLines.push(ele);
        }
      });
  }

  private exogenousInTableData(exog: ExogenousResponse): boolean {
    let inTable = false;
    this.data
      .filter(s => !this.isEmptyExog(s))
      .forEach((ele, index) => {
        if (ele.Exogenous_Sid === exog.Exogenous_Sid) {
          inTable = true;
        }
      });
    return inTable;
  }

  private checkStoredRecordsForChanges(exog: ExogenousResponse): boolean {
    let isChangedRecord = false;

    const storedExog = this.storedExogenousLines.find(s => s.Exogenous_Sid === exog.Exogenous_Sid);

    if (storedExog) {
      if (!this.checkInsertToStoredRecordForMatch(exog, storedExog)) {
        isChangedRecord = true;
      }
    }
    return isChangedRecord;
  }

  private checkInsertToStoredRecordForMatch(inserted: ExogenousResponse, stored: ExogenousResponse): boolean {
    return (
      inserted.Name === stored.Name &&
      inserted.Mnemonic === stored.Mnemonic &&
      inserted.Group_ID === stored.Group_ID &&
      inserted.Account_Number === stored.Account_Number &&
      inserted.Description === stored.Description &&
      inserted.Display_Format === stored.Display_Format &&
      inserted.Calculate_Group === stored.Calculate_Group &&
      inserted.Simulation_Order === stored.Simulation_Order &&
      inserted.Display_Order === stored.Display_Order &&
      inserted.Risk_Limit === stored.Risk_Limit &&
      inserted.Fail_Operator === stored.Fail_Operator &&
      inserted.Display_In_Summary === stored.Display_In_Summary
    );
  }

  private checkForCutPastedNullRecords(exog: ExogenousResponse): boolean {
    return exog.Exogenous_Sid > 0 && exog.Account_Number === null && exog.Mnemonic === null && exog.Name === null;
  }

  private showSuccessToast() {
    this.notifications.hideToasts();
    const pageTitle = 'Exogenous Lines';
    this.notifications.showToastWithButtons(
      ToastLevel.Success,
      'Success',
      `${pageTitle} successfully updated!`,
      [
        {
          name: 'View Period Equations',
          class: 'btn_normal btn_outline_white btn-toast-action',
          action: () => {
            this.menu.navigateTo('exogenous-equations');
          }
        }
      ]
    );
  }

  private showFailToast() {
    const toasts = this.notifications.numberOfActiveToasts;

    // Not showing several toasts when there are several simultaneous saved changes (i.e. add + update + delete).
    if (toasts === 0) {
      const pageTitle = 'Exogenous Lines';
      this.notifications.showToast(ToastLevel.Error, {  message: `Error updating ${pageTitle}!` } as Toast);
    }
  }

  // TODO: Centralize
  private showDiscardToast() {
    const body = `You have made changes that may cause data to be lost. Press 'Save' to confirm, or 'Discard'. `;
    const discardToast: Toast = {      
      title: '',
      message: body,
    } as Toast;
    this.notifications.hideToasts();
    this.notifications.showToast(ToastLevel.Warning, discardToast, 'Discard', 'exogenous-lines-discard');
  }

  private discardChanges() {
    this.loadData();
  }

  private checkDataLoss() {
    this.willCauseDataLoss = this.deletedExogenousLines.length > 0;

    if (this.willCauseDataLoss) {
      const isAdmin = isLiquidityAdmin(this.auth.userLevel);

      if (isAdmin) {
        this.showDiscardToast();
      }
    }
  }

  private isValidSheet(): boolean {
    // Executed one time for each row
    this.invalidRows = [];
    this.hasInvalidations = false;
    this.validSheet = false;

    // Each invalid row index will be pushed into 'invalidRows' array
    this.data.forEach((acc, rowIndex) => {
      if (!this.isEmptyRow(rowIndex)) {
        this.validateRow(rowIndex);
      }
      this.invalidationsCheck(rowIndex);
    });

    // Assign a value to validSheet
    this.validSheet = !this.hasInvalidations && this.invalidRows.length === 0;

    return this.validSheet;
  }

  private invalidationsCheck(rowIndex = -1): void {
    const rowInvalidationCheck = (_rowIndex: number) => {
      // Check if some of the fields are invalid
      Object.keys(this.data[_rowIndex]).forEach((colName, col_index) => {
        const validField = this.sheet.isValid(_rowIndex, col_index, this.sheet.getCell(_rowIndex, col_index).value());
        if (!validField) {
          this.hasInvalidations = true;
        }
      });
    };

    if (rowIndex > -1) {
      rowInvalidationCheck(rowIndex);
    } else {
      this.data.forEach((acc, row_index) => rowInvalidationCheck(row_index));
    }

    // Neccessary to avoid the save button blocking behavior when the mouse has not move (related: onMouseMove)
    this.menu.emitResize();
    // return changed;
  }

  private validateRow(r: number) {
    const {
      Name,
      Mnemonic,
      Group_ID,
      Account_Number,
      Description,
      Display_Format,
      Calculate_Group,
      Simulation_Order,
      Display_Order,
      Fail_Operator,
      Risk_Limit,
      Display_In_Summary,
    } = <ExogenousLine>this.data[r];

    const isEmpty = this.isEmptyRow(r);
    const validAccountName = this.sheet.isValid(r, 0, Name);
    const validMnemonic = this.sheet.isValid(r, 1, Mnemonic);
    const validAccountNumber = this.sheet.isValid(r, 3, Account_Number);
    const validCalcGroup = this.sheet.isValid(r, 6, Calculate_Group);
    const validDisplayOrder = this.sheet.isValid(r, 8, Display_Order);
    const validFailOperator = this.sheet.isValid(r, 9, Fail_Operator);
    const validRiskLimit = this.sheet.isValid(r, 10, Risk_Limit);
    const validDisplayInSummary = this.sheet.isValid(r, 11, Display_In_Summary);

    // Row is also valid if empty
    const valid =
      isEmpty ||
      (validAccountName &&
        validMnemonic &&
        validAccountNumber &&
        validCalcGroup &&
        validDisplayOrder &&
        validFailOperator &&
        validRiskLimit &&
        validDisplayInSummary);

    // if (!validCategory) {
    //   this.addCategoryValidatorToRow(r);
    // }

    // So ngOnChange gets triggered and invalidRows update is detected
    this.zone.run(() => {
      if (valid) {
        // Remove this row from the invalidRows array
        this.invalidRows = [...this.invalidRows].filter(x => x !== r + 1);
      } else {
        // Add this row to the invalidRows array
        this.invalidRows = [...this.invalidRows, r + 1];
      }
    });

    return valid;
  }

  public onMouseMove(event) {
    const { layerX: x, layerY: y } = event;
    // const x = layerX;
    // const y = layerY;

    const target = this.sheet.hitTest(x, y);
    if (target) {
      const { row, col } = target;
      // "IF" clause to avoid showing error messages on non-content areas of the spreadsheet
      if (row > -1 && col > -1) {
        const thisCell = this.sheet.getCell(row, col);
        const value = thisCell.value();
        const validator = thisCell.validator();
        const isValid = this.sheet.isValid(row, col, value);
        this.notification = {};
        const columnsWithValidations = [0, 1, 3, 6, 8, 9, 10, 11];

        let validationMessageIndex;
        switch (col) {
          case 0:
            validationMessageIndex = 0;
            break;
          case 1:
            validationMessageIndex = 1;
            break;
          case 3:
            validationMessageIndex = 2;
            break;
          case 6:
            validationMessageIndex = 3;
            break;
          case 8:
            validationMessageIndex = 4;
            break;
          default:
            validationMessageIndex = -1;
        }
        const validationMessages = [
          {
            field: 'Name',
            message: value ? `The Name cannot include wildcard characters as: ?, *, ~` : `Name cannot be empty`,
          },
          {
            field: 'Mnemonic',
            message: value ? `The Mnemonic should be Unique\nValid characters: a-z, A-Z, 0-9, _` : `Mnemonic cannot be empty`,
          },
          {
            field: 'Account Number',
            message: value ? `The Account Number should be an unique number >= 0` : `Account Number can't be empty`,
          },
          {
            field: 'Calculate Group',
            message: `Calculate Group should be a whole number higher or equal than 0`,
          },
          {
            field: 'Display Order',
            message: `Display Order should be numeric`,
          },
          {
            field: 'Fail Operator',
            message: `Fail Operator should be <, > or blank`,
          },
          {
            field: 'Risk Limit',
            message: `Risk Limit should be numeric`,
          },
          {
            field: 'Display In Summary',
            message: `Display In Summary should be 'Yes' or 'No'`,
          },
        ];

        const cell = validationMessages[validationMessageIndex];

        if (validator && cell && !isValid) {
          if (columnsWithValidations.includes(col)) {
            this.notification = {
              title: `Invalid ${cell.field}`,
              value: value,
              body: `${cell.message}`,
              type: 'error',
            };
          }
        }
      }
    }
  }

  private isBlankOrInvalidExog(exog: ExogenousLine) {
    return !exog || !exog.Mnemonic || !exog.Account_Number;
  }

  async canDeactivate(): Promise<boolean> {
    return this.menu.canDeactivatePopup();
  }
}
